import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MessageType } from 'enums/MessageType';
import { VariantListTypes } from 'enums/VariantListTypes';
import { environment } from 'environments/environment';
import 'extensions/deep-copy';
import { OrderModel } from 'models/generated/Entities.GormCrm.Order';
import { OrderLineModel } from 'models/generated/Entities.GormCrm.OrderLine';
import { OrderProductPlanDiscontinuationModel } from 'models/generated/Entities.GormCrm.OrderProductPlanDiscontinuation';
import { ProductPlanModel } from 'models/generated/Entities.GormCrm.ProductPlan';
import { SaleAgreementModel } from 'models/generated/Entities.GormCrm.SaleAgreement';
import { NewOrderRequestModel } from 'models/generated/Models.NewOrderRequest';
import { OrderCancellationRequestModel } from 'models/generated/Models.OrderCancellationRequest';
import { OrderInvoiceRequestModel } from 'models/generated/Models.OrderInvoiceRequest';
import { OrderWarningRequestModel } from 'models/generated/Models.OrderWarningRequest';
import { SaleAgreementRequestModel } from 'models/generated/Models.SaleAgreementRequest';
import { firstValueFrom } from 'rxjs';
import { CompanyService } from 'services/company.service';
import { LiveDataTracker, TrackingContext } from 'services/data-tracking';
import { PushService } from 'services/push.service';

export type OrderStatus = 'approved' | 'unapproved' | 'warning' | 'cancelled' | 'incomplete';
export interface OrderSearchFilter {
	sellerId?: string,
	companyId?: string,
	status?: OrderStatus
}

@Injectable()
export class SalesService {

	private baseGwUrl = `${environment.gatewayUrl}/api/gateway/order`;
	private baseUrl = `${environment.gatewayUrl}/api/orders`;
	private baseUrlUpload = `${environment.gatewayUrl}/api/upload`;
	private agreementUrl = `${environment.gatewayUrl}/api/sale-agreements`;
	private orderTracker = new LiveDataTracker<OrderModel, { orderId: string, companyId?: string, userId?: string }>(plan => plan.id!);

	constructor(private readonly http: HttpClient, private companyService: CompanyService, pushService: PushService) {
		pushService.onChanges<{ orderId: string }>(MessageType.OrderChanges).subscribe(event => {
			const orderId = event.data?.orderId;
			if (orderId) this.orderTracker.refreshData(event, orderId, () => this.getOrderById(orderId));
		});
	}

	getOrders(filter: OrderSearchFilter, trackingContext?: TrackingContext): Promise<OrderModel[]> {
		return firstValueFrom(this.http.get<OrderModel[]>(`${this.baseUrl}`, { params: filter as HttpParams }))
			.then(orders => this.orderTracker.trackArrayForComponent(trackingContext, orders, (pushData) => {
				return (filter.companyId === undefined || filter.companyId == pushData.companyId) &&
					(filter.sellerId === undefined || filter.sellerId == pushData.userId) &&
					(filter.status === undefined || filter.status == 'unapproved') // New orders are always unapproved
			}));
	}

	getTotalPrice(order: OrderModel): number {
		let price = 0;
		for (const orderLine of order.orderLines) {
			price += orderLine.totalPrice - (orderLine.discount ?? 0);
		}
		for (const discontinuedPlan of order.orderProductPlanDiscontinuations) {
			price -= discontinuedPlan.adjustmentAmount ?? 0;
		}
		return price;
	}

	approveOrder(orderId: string): Promise<void> {
		return firstValueFrom(this.http.get<void>(`${this.baseUrl}/${orderId}/approve`));
	}

	getOrderStatus(order: OrderModel): OrderStatus {
		if (order.orderCancellations?.length > 0) return 'cancelled';
		if (order.approvedDate) return 'approved';
		if (order.orderWarnings?.filter(w => !w.closed).length > 0) return 'warning';
		return 'unapproved';
	}

	getOrderSalesType(order: OrderModel): string {
		if (order.companyHadActivePlans || order.externalAnnualValueForRenewal !== undefined || order.externalRemainingAmount !== undefined)
			return 'existing';
		return 'new';
	}

	getOrderById(orderId: string, trackingContext?: TrackingContext): Promise<OrderModel> {

		return firstValueFrom(this.http.get<OrderModel>(`${this.baseUrl}/${orderId}`))
			.then(order => this.orderTracker.trackObjectForComponent(trackingContext, order));
	}

	createOrUpdateSaleAgreement(companyId: string, ownerId: string, comment: string, agreementId?: string): Promise<void> {
		const request = new SaleAgreementRequestModel();
		request.comment = comment;
		if (agreementId) {
			return firstValueFrom(this.http.put<void>(`${this.agreementUrl}/${companyId}/${ownerId}/${agreementId}`, request));
		} else {
			return firstValueFrom(this.http.post<void>(`${this.agreementUrl}/${companyId}/${ownerId}`, request));
		}
	}

	getCompanySaleAgreements(companyId: string, userId?: string): Promise<SaleAgreementModel[]> {
		if (userId) return firstValueFrom(this.http.get<SaleAgreementModel[]>(`${this.agreementUrl}/${companyId}/${userId}`));
		else return firstValueFrom(this.http.get<SaleAgreementModel[]>(`${this.agreementUrl}/${companyId}`));
	}

	deleteSaleAgreement(saleAgreement: SaleAgreementModel): Promise<void> {
		return firstValueFrom(this.http.delete<void>(`${this.agreementUrl}/${saleAgreement.id}`));
	}

	deleteSalesAttachment(attachmentId:string): Promise<void> {
		return firstValueFrom(this.http.delete<void>(`${this.baseUrlUpload}/remove-sale-attachment/${attachmentId}`));
	}

	updateSaleAttachments(orderId: string, fileIds: string[]): Promise<void> {
		return firstValueFrom(this.http.put<void>(`${this.baseUrl}/${orderId}/link-attachments`, fileIds));
	}

	verifyCompanyName(order: OrderModel): boolean {
		if (!order.company) return false;
		return this.companyService.verifyCompanyName(order.company);
	}

	verifyCompanyAddress(order: OrderModel): boolean {
		if (!order.company) return false;
		return this.companyService.verifyCompanyAddress(order.company);
	}

	verifyContractUploaded(order: OrderModel): boolean {
		if (order.saleAttachments.length > 0) return true;
		return false;
	}

	createOrder(order: NewOrderRequestModel): Promise<void> {
		order.include(o => o.company);
		order.company?.include(c => c.replies);
		order.include(o => o.contact);
		order.include(o => o.products);
		order.include(o => o.productPlanDiscontinuations);
		order.products.forEach(product => { product.include(o => o.childLines); product.include(o => o.replies); });
		order.include(o => o.replies);
		order.include(o => o.seller);
		return firstValueFrom(this.http.post<void>(`${this.baseGwUrl}`, order));
	}

	updateOrderDetails(order: OrderModel): Promise<void> {
		return firstValueFrom(this.http.put<void>(`${this.baseUrl}`, order));
	}

	createOrderCancellation(orderId: string, comment: string, reasonId: string): Promise<void> {
		const request = new OrderCancellationRequestModel();
		request.comment = comment;
		request.reasonId = reasonId;
		return firstValueFrom(this.http.post<void>(`${this.baseUrl}/${orderId}/cancellation`, request));
	}

	createOrderWarning(orderId: string, comment: string, reasonId: string): Promise<void> {
		const request = new OrderWarningRequestModel();
		request.comment = comment;
		request.reasonId = reasonId;
		return firstValueFrom(this.http.post<void>(`${this.baseUrl}/${orderId}/warning`, request));
	}

	setWithholdCommission(orderId: string, withholdCommission: boolean): Promise<void> {
		return firstValueFrom(this.http.patch<void>(`${this.baseUrl}/${orderId}/withhold-commission`, withholdCommission));
	}

	setAutoDelivery(orderId: string, autoDelivery: boolean): Promise<void> {
		return firstValueFrom(this.http.patch<void>(`${this.baseUrl}/${orderId}/auto-delivery`, autoDelivery));
	}

	updateInstallments(orderId: string, numberOfInstallments?: number, startupInstallments?: number): Promise<void> {
		return firstValueFrom(this.http.put<void>(`${this.baseUrl}/${orderId}/update-installments`, {}, { params: { numberOfInstallments: numberOfInstallments ?? '', startupInstallments: startupInstallments ?? '' } }));
	}

	updateDiscount(orderId: string, discountAmount?: number, discountReasonId?: string) {
		return firstValueFrom(this.http.put<void>(`${this.baseUrl}/${orderId}/update-discount`, {}, { params: { discountAmount: discountAmount ?? '', discountReasonId: discountReasonId ?? '' } }));
	}

	updateExternals(orderId: string, externalRemainingAmount?: number, externalAnnualValueForRenewal?: number) {
		return firstValueFrom(this.http.put<void>(`${this.baseUrl}/${orderId}/update-externals`, {}, { params: { externalRemainingAmount: externalRemainingAmount ?? '', externalAnnualValueForRenewal: externalAnnualValueForRenewal ?? '' } }));
	}

	updateOrder(orderId: string, orderLines: OrderLineModel[], discontinuations: OrderProductPlanDiscontinuationModel[]): Promise<void> {

		// Use actual OrderModel instance so we can rely on RelationService to remove unnecessary deep entity data (only reference we need on orderLines is the productVariantId)
		const orderRequest = new OrderModel();
		orderRequest.orderLines = orderLines;
		orderRequest.orderProductPlanDiscontinuations = discontinuations;
		orderRequest.include(o => o.orderLines);
		orderRequest.include(o => o.orderProductPlanDiscontinuations);

		// Copy the request object to avoid destroying the client view when relation properties are removed. RelationService (used in HTTP interceptor) should always do this, but that would create an enormous test overhead right now
		return firstValueFrom(this.http.patch<void>(`${this.baseUrl}/${orderId}`, Object.deepCopy(orderRequest)));
	}

	closeOrderWarning(orderWarningId: string) {
		return firstValueFrom(this.http.get<void>(`${this.baseUrl}/warnings/${orderWarningId}/close`));
	}

	getCompanyProducts(companyId: string): Promise<OrderLineModel[]> {
		return this.companyService.getCompanyProducts(companyId);
	}

	getCompanyProductsByOrderLineIds(orderLineIds: string[]): Promise<OrderLineModel[]> {
		return firstValueFrom(this.http.post<OrderLineModel[]>(`${this.baseUrl}/products/get-by-ids`, orderLineIds));
	}

	getOrdersByIds(orderIds: string[], trackingContext?: TrackingContext): Promise<OrderModel[]> {
		return firstValueFrom(this.http.post<OrderModel[]>(`${this.baseUrl}/get-by-ids`, orderIds))
			.then(orders => this.orderTracker.trackArrayForComponent(trackingContext, orders));
	}

	invoiceOrderLines(orderLineIds: string[], taskActionId: string): Promise<void> {
		const request = new OrderInvoiceRequestModel();
		request.orderLineIds = orderLineIds;
		request.taskActionId = taskActionId;
		return firstValueFrom(this.http.post<void>(`${this.baseUrl}/products/request-invoice`, request));
	}

	createProductPlanFromOrderLines(orderLines: OrderLineModel[], companyId: string, brandId: string) {

		const order = new OrderModel();
		order.orderLines.push(...orderLines);
		this.calculateStartupInclFirstInvoice(order);

		for (const orderLine of orderLines) orderLine.include(ol => ol.productVariant);
		return firstValueFrom(this.http.post<ProductPlanModel[]>(`${this.baseUrl}/create-product-plan-from-order-lines`, orderLines, { params: { companyId, brandId } }));
	}

	calculateStartupInclFirstInvoice(order: OrderModel): void {
		const counterValue = order.orderProductPlanDiscontinuations.map(d => d.adjustmentAmount ?? 0).reduce((sum, amount) => sum + amount, 0);
		const totalOrderValue = order.orderLines.map(ol => ol.totalPrice - (ol.discount ?? 0)).reduce((sum, amount) => sum + amount, 0);
		const totalOrderPrice = totalOrderValue - counterValue;

		if (totalOrderPrice <= 0) return;

		for (const orderLine of order.orderLines) {
			const fractionOfTotalValue = (orderLine.totalPrice - (orderLine.discount ?? 0)) / totalOrderValue;
			const orderLineActualPrice = totalOrderPrice * fractionOfTotalValue;
			this.calculateStartupInclFirstInvoiceForOrderLine(orderLine, orderLineActualPrice);
		}
	}

	private calculateStartupInclFirstInvoiceForOrderLine(orderLine: OrderLineModel, actualPrice: number): void {
		if (!orderLine.productVariant || orderLine.startupAmountExclFirstInvoice == undefined) {
			orderLine.startupAmount = undefined;
			return;
		}

		const remainingTotalPrice = actualPrice - orderLine.startupAmountExclFirstInvoice;
		const periodVariantOption = orderLine.productVariant.productVariantsOptions.find(pvo => pvo.variantOption?.variantList?.type === VariantListTypes.Period)?.variantOption;

		const period = parseInt(periodVariantOption?.name ?? '');
		let firstInvoiceAmount;
		// If variant does not have a period, "the first invoice" will be considered the total price of the orderline (even if it's actually broken into multiple installments)
		if (isNaN(period) || period <= 0) firstInvoiceAmount = remainingTotalPrice;
		else firstInvoiceAmount = remainingTotalPrice * orderLine.paymentInterval / period;

		orderLine.startupInstallments = orderLine.startupInstallments ?? 1;
		orderLine.startupAmount = orderLine.startupAmountExclFirstInvoice + firstInvoiceAmount;
		orderLine.startupAmount = Math.round(orderLine.startupAmount * 100) / 100; // Ensure proper rounded value with two decimals
	}

	getOrderLinesDiscountSum(orderLines: OrderLineModel[]): number {
		return orderLines.map(ol => ol.discount).reduce((sum, amount) => (sum ?? 0) + (amount ?? 0), 0) ?? 0;
	}

	getOrderLinesTotalPriceSum(orderLines: OrderLineModel[]): number {
		return orderLines.reduce((sum, orderLine) => sum + orderLine.totalPrice, 0) ?? 0;
	}

	getTotalAdjustmentAmount(order: OrderModel): number {
		return order.orderProductPlanDiscontinuations.reduce((sum, discontinuedPlan) => sum - (discontinuedPlan.adjustmentAmount ?? 0), 0) ?? 0;
	}

	calculateSupersededRemainingAmountForOrder(order: OrderModel): number {
		return (order.orderProductPlanDiscontinuations.reduce((sum, discontinuedPlan) => sum - (discontinuedPlan.remainingAmount ?? 0), 0) ?? 0) - (order.externalRemainingAmount ?? 0);
	}

	hasExternalAdjustments(order: OrderModel): boolean {
		return order.externalAnnualValueForRenewal !== undefined || order.externalRemainingAmount !== undefined || order.discountReasonId !== undefined || (order.orderLines?.some(ol => ol.discount !== undefined) ?? false);
	}

	getAcvIncreaseCommission(orderLines: OrderLineModel[]): number {
		return orderLines.reduce((sum, orderLine) => sum + (orderLine.orderLineCommission?.acvIncreaseCommissionEnabled ? orderLine.orderLineCommission?.acvIncrease ?? 0 : 0), 0) ?? 0;
	}

	getAcvExtendCommission(orderLines: OrderLineModel[]): number {
		return orderLines.reduce((sum, orderLine) => sum + (orderLine.orderLineCommission?.acvExtendCommissionEnabled ? orderLine.orderLineCommission?.acvExtend ?? 0 : 0), 0) ?? 0;
	}

	getUpFrontCommission(orderLines: OrderLineModel[]): number {
		return orderLines.reduce((sum, orderLine) => sum + (orderLine.orderLineCommission?.upFrontCommissionEnabled ? orderLine.orderLineCommission?.upFront ?? 0 : 0), 0) ?? 0;
	}
}
